// RevenueCat on the device. Client side of the paywall (2026-10-27).
//
// Every export is a no-op off the native app: /demo and the web never load
// the SDK (dynamic import, so the web bundle does not even carry it). The
// SDK is configured ONCE per JS lifetime with the Supabase user id as the
// RevenueCat app user id, so the server (api/lib/revenuecat.js) can map a
// webhook straight to profiles.id; a later account on the same device goes
// through logIn, and sign out / deletion call logOut.
//
// The public SDK key is public by design (it only identifies the app; the
// secret key stays server side).
import { Capacitor } from '@capacitor/core';

export const RC_PUBLIC_KEY = 'appl_WRZdZzAmBwOhQvMsHdjRBOZncQY';
export const APPLE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';

let sdkPromise = null;
let sdkConfigured = false;
let currentUserId = null;

const isNative = () => Capacitor.isNativePlatform();

async function sdk() {
  if (!sdkPromise) sdkPromise = import('@revenuecat/purchases-capacitor');
  return sdkPromise;
}

/** Configure once, then logIn on an account switch. Returns true when the SDK is bound to userId. */
export async function configurePurchases(userId) {
  if (!isNative() || !userId) return false;
  const { Purchases } = await sdk();
  if (!sdkConfigured) {
    await Purchases.configure({ apiKey: RC_PUBLIC_KEY, appUserID: String(userId) });
    sdkConfigured = true;
    currentUserId = String(userId);
    return true;
  }
  if (currentUserId !== String(userId)) {
    await Purchases.logIn({ appUserID: String(userId) });
    currentUserId = String(userId);
  }
  return true;
}

/** Sign out / account deletion. Never throws: the auth sign out must not wait on this. */
export async function logOutPurchases() {
  if (!isNative() || !sdkConfigured || !currentUserId) return;
  try {
    const { Purchases } = await sdk();
    await Purchases.logOut();
  } catch (e) {
    console.log('purchases: logOut error', e?.message || e);
  }
  currentUserId = null;
}

/** offerings.current (null when RevenueCat has no current offering). */
export async function getOfferings() {
  if (!isNative()) return null;
  const { Purchases } = await sdk();
  const { current } = await Purchases.getOfferings();
  return current || null;
}

/** True when the thrown error is the user backing out of the App Store sheet. */
export function isUserCancelled(err, PURCHASES_ERROR_CODE) {
  if (!err) return false;
  if (err.userCancelled === true) return true;
  const cancelCode = PURCHASES_ERROR_CODE?.PURCHASE_CANCELLED_ERROR;
  if (cancelCode != null && String(err.code) === String(cancelCode)) return true;
  return false;
}

/**
 * Buy a package. Resolves { cancelled: true } when the user backed out,
 * { cancelled: false, customerInfo } on success; throws on a real failure.
 */
export async function purchase(pkg) {
  if (!isNative()) throw new Error('purchases: not native');
  const { Purchases, PURCHASES_ERROR_CODE } = await sdk();
  try {
    const result = await Purchases.purchasePackage({ aPackage: pkg });
    return { cancelled: false, customerInfo: result?.customerInfo || null };
  } catch (e) {
    if (isUserCancelled(e, PURCHASES_ERROR_CODE)) return { cancelled: true };
    throw e;
  }
}

export async function restore() {
  if (!isNative()) throw new Error('purchases: not native');
  const { Purchases } = await sdk();
  const result = await Purchases.restorePurchases();
  return result?.customerInfo || null;
}

/** productId -> true only when StoreKit says the intro offer is available to this Apple ID. */
export async function checkTrialEligibility(productIds) {
  const ids = (productIds || []).filter(Boolean);
  if (!isNative() || ids.length === 0) return {};
  const { Purchases, INTRO_ELIGIBILITY_STATUS } = await sdk();
  const map = await Purchases.checkTrialOrIntroductoryPriceEligibility({ productIdentifiers: ids });
  const eligible = INTRO_ELIGIBILITY_STATUS?.INTRO_ELIGIBILITY_STATUS_ELIGIBLE ?? 2;
  const out = {};
  for (const id of ids) out[id] = map?.[id]?.status === eligible;
  return out;
}

/**
 * Opens Apple's subscription management. purchases-capacitor 13.8 has no
 * showManageSubscriptions call, so this uses the customer's managementURL
 * from RevenueCat and falls back to the App Store subscriptions page.
 */
export async function showManageSubscriptions() {
  let url = APPLE_SUBSCRIPTIONS_URL;
  if (isNative()) {
    try {
      const { Purchases } = await sdk();
      const { customerInfo } = await Purchases.getCustomerInfo();
      if (customerInfo?.managementURL) url = customerInfo.managementURL;
    } catch (e) {
      console.log('purchases: managementURL unavailable', e?.message || e);
    }
  }
  window.open(url, '_blank');
}
